import React, { memo, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import ModalWindow from '@common/components/modal/modal-window';
import ModalContent from '@common/components/modal/modal-content';
import { Button } from '@common/components/button';
import { useAppSelector } from '@common/hooks';
import * as userSelector from '../../../core/selectors/logged-user';
import { EPermissions } from '@common/definitions';
import { Moment } from 'moment';
import * as organisationSelector from '../../../organisation/selectors/organisation';
import * as networkSelector from '../../../network/selectors/network';
import { useRequest } from '@common/services/api';
import { useCalendar } from '@modules/calendar/context/calendar-context';

type ScheduleModelProps = {
  currentWeek: Moment;
};

const ScheduleModel = memo(({ currentWeek }: ScheduleModelProps) => {
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const organisation = useAppSelector(organisationSelector.selected);
  const { refetchSchedules } = useCalendar();
  if (
    organisation?.integrations?.length &&
    organisation.integrations.find(
      (integration) => integration.external_schedule
    )
  ) {
    return null;
  }
  const network = useAppSelector((state) => networkSelector.selected(state));
  const functions = useAppSelector((state) => userSelector.functions(state, EPermissions.NETWORK_SCHEDULES_VIEW_ALL));
  const { t } = useTranslation();
  const week = currentWeek.isoWeek();
  const year = currentWeek.isoWeekYear();

  const { data: schedules, loading: loadingSchedules, refetch: refetchSchedulesRequest } = useRequest(
    `/v2/organisations/${organisation?.id}/calendar/schedules`,
    {
      payload: { week, year },
    }
  );
  useEffect(() => {
    if (organisation?.id) {
      refetchSchedulesRequest();
    }
  }, [week, year, organisation?.id, refetchSchedules]);

  const filteredSchedules = useMemo(() => {
    return schedules?.filter((schedule: any) => schedule.network_id === network?.id).map((schedule: any) => ({
      ...schedule,
      network,
      function: schedule.function_id
        ? functions?.find((f: any) => f.id === schedule.function_id)
        : null,
    }));
  }, [schedules, network, functions]);
  const handleHide = () => {
    setShowScheduleModal(false);
  };

  return (
    <>
      {filteredSchedules.length > 0 && (
        <Button type="white" size="default" isLoading={loadingSchedules} onClick={() => setShowScheduleModal(true)}>
          <Trans i18nKey="calendar:view_week_schedules" />
        </Button>
      )}
      <ModalWindow show={showScheduleModal} onHide={handleHide}>
        <ModalContent
          title={t('calendar:week_schedules')}
          hideFooter
          onHide={handleHide}
        >
          <div className="BirthdaysList ">
            {filteredSchedules?.map((schedule: any) => (
              <div
                className={`tw-flex tw-items-center tw-justify-between tw-border-0 last:tw-border-b-0 tw-border-b 
                tw-border-solid tw-border-gray-200 tw-mx-[20px] tw-py-[12px]`}
                key={schedule.id}
              >
                <span>{schedule.function?.name ?? schedule.network?.name}</span>
                <a href={schedule.path} target="_blank" rel="noreferrer">
                  <Button type="primary">
                    <Trans i18nKey="schedules:view_button" />
                  </Button>
                </a>
              </div>
            ))}
          </div>
        </ModalContent>
      </ModalWindow>
    </>
  );
});

export default ScheduleModel;
